import React, { useEffect, useMemo, useState } from 'react'
import { Nav, SelectPicker, Tag } from 'rsuite'
import { AVAILABLE_LANGUAGES, DEFAULT_LOCALES, getLanguageByCode } from '@@partner:config/locale'

import './SelectLocale.scss'

type SelectLocaleProps = {
  languages: string[]
  selectedLanguage?: string
  onSelect: (language: string) => void
  onAdd: (language: string) => void
  onRemove?: (language: string) => void
  /** Показывать только флаг и код (компактный режим) */
  compact?: boolean
}

export default function SelectLocale ({
  languages = [],
  selectedLanguage,
  onSelect,
  onAdd,
  onRemove,
  compact = false
}: SelectLocaleProps) {
  const [ showPicker, setShowPicker ] = useState(false)

  // При первом рендере добавляем языки из DEFAULT_LOCALES если их нет
  useEffect(() => {
    if (languages.length === 0 && DEFAULT_LOCALES.length > 0) {
      // Добавляем все языки из DEFAULT_LOCALES
      DEFAULT_LOCALES.forEach(code => {
        onAdd(code)
      })
      // Выбираем первый язык
      onSelect(DEFAULT_LOCALES[0])
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Языки которые еще не добавлены
  const availableToAdd = useMemo(() => {
    return AVAILABLE_LANGUAGES.filter(lang => !languages.includes(lang.code))
  }, [ languages ])

  // Данные для SelectPicker
  const pickerData = useMemo(() => {
    // Сначала предложенные языки из DEFAULT_LOCALES которых еще нет
    const suggested = DEFAULT_LOCALES
      .filter(code => !languages.includes(code))
      .map(code => {
        const lang = getLanguageByCode(code)
        return {
          value: code,
          label: lang ? `${lang.flag} ${lang.name}` : code,
          group: 'Рекомендуемые'
        }
      })

    // Остальные доступные языки
    const others = availableToAdd
      .filter(lang => !DEFAULT_LOCALES.includes(lang.code))
      .map(lang => ({
        value: lang.code,
        label: `${lang.flag} ${lang.name}`,
        group: 'Все языки'
      }))

    return [ ...suggested, ...others ]
  }, [ availableToAdd, languages ])

  const handleAddLanguage = (code: string | null) => {
    if (code) {
      onAdd(code)
      setShowPicker(false)
    }
  }

  const renderLanguageTab = (code: string) => {
    const lang = getLanguageByCode(code)
    if (compact) {
      return lang ? `${lang.flag} ${lang.code.toUpperCase()}` : code.toUpperCase()
    }
    return lang ? `${lang.flag} ${lang.name}` : code.toUpperCase()
  }

  return (
    <div className="SelectLocale">
      <Nav appearance="subtle" className="SelectLocale__tabs">
        {languages.map((code) => (
          <Nav.Item
            key={code}
            active={selectedLanguage === code}
            onSelect={() => onSelect(code)}
            className="SelectLocale__tab"
          >
            {renderLanguageTab(code)}
            {onRemove && languages.length > 1 && (
              <Tag
                size="sm"
                className="SelectLocale__remove"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemove(code)
                }}
              >
                ×
              </Tag>
            )}
          </Nav.Item>
        ))}

        {!showPicker && availableToAdd.length > 0 && (
          <Nav.Item
            className="SelectLocale__add-btn"
            onClick={() => setShowPicker(true)}
          >
            + Добавить язык
          </Nav.Item>
        )}

        {showPicker && (
          <div className="SelectLocale__picker-wrapper">
            <SelectPicker
              data={pickerData}
              groupBy="group"
              placeholder="Выберите язык"
              searchable
              size="sm"
              onSelect={handleAddLanguage}
              onClean={() => setShowPicker(false)}
              onClose={() => setShowPicker(false)}
              style={{ width: 200 }}
              autoFocus
              defaultOpen
            />
          </div>
        )}
      </Nav>
    </div>
  )
}